import { OrderDelivery } from '@restaurant/api';
import { OrderType } from '@restaurant/types';
import {
  Button,
  Modal,
  Form,
  Input,
  Row,
  Col,
  DatePicker,
  TimePicker,
} from 'antd';
import moment, { Moment } from 'moment';
import React, { useContext, useMemo, useState } from 'react';
import { OrderContext } from '~/App/Layout/providers/OrderProvider';
import { OrderDeliveryFormValues } from './types';
import {
  transformFromDeliveryFormValues,
  transformToDeliveryFormValues,
} from './utils';
import { EditOutlined } from '@ant-design/icons';

const requiredRules = [{ required: true, message: 'Wajib diisi' }];

const tomorrow = moment().add(1, 'days').startOf('day');

export const DeliveryFormFields = ({ prefix }: { prefix?: string[] }) => {
  const getPrefix = (field: string | string[]) => {
    if (!prefix?.length) return field;

    if (Array.isArray(field)) {
      return [...prefix, ...field];
    }
    return [...prefix, field];
  };

  return (
    <>
      <Form.Item
        name={getPrefix('recipientName')}
        label="Nama"
        rules={requiredRules}
      >
        <Input placeholder="Nama" />
      </Form.Item>
      <Form.Item
        name={getPrefix('mobileNumber')}
        label="No. HP"
        rules={requiredRules}
      >
        <Input prefix="+62" placeholder="No. HP" />
      </Form.Item>
      <p className="mb-4 pt-2">Waktu pengambilan</p>
      <div className="flex gap-4">
        <Form.Item
          name={getPrefix(['deliveryDateTime', 'date'])}
          rules={requiredRules}
        >
          <DatePicker disabledDate={(current) => current < tomorrow} />
        </Form.Item>
        <Form.Item name={getPrefix(['deliveryDateTime', 'time'])}>
          <TimePicker format="HH:mm" minuteStep={5} />
        </Form.Item>
      </div>
      <Form.Item
        label="Alamat"
        rules={requiredRules}
        name={getPrefix(['deliveryAddress', 'streetAddressLine1'])}
      >
        <Input placeholder="Alamat" />
      </Form.Item>
      <Form.Item name={getPrefix(['deliveryAddress', 'streetAddressLine2'])}>
        <Input />
      </Form.Item>
      <Form.Item
        name={getPrefix(['deliveryAddress', 'subdistrict'])}
        label="Kelurahan"
      >
        <Input placeholder="Kelurahan" />
      </Form.Item>
      <Form.Item
        name={getPrefix(['deliveryAddress', 'district'])}
        label="Kecamatan"
      >
        <Input placeholder="Kecamatan" />
      </Form.Item>
      <Form.Item name={getPrefix(['deliveryAddress', 'city'])} label="Kota">
        <Input placeholder="Kota" />
      </Form.Item>
      <Form.Item
        name={getPrefix(['deliveryAddress', 'province'])}
        label="Provinsi"
      >
        <Input placeholder="Provinsi" />
      </Form.Item>
      <Form.Item
        name={getPrefix(['deliveryAddress', 'postalCode'])}
        label="Kode Pos"
      >
        <Input placeholder="Kode pos" />
      </Form.Item>
    </>
  );
};

const InputDeliveryModal = ({
  initialValues,
  onSubmit,
}: {
  initialValues: OrderDelivery | null;
  onSubmit: (values: OrderDelivery) => void;
}) => {
  const handleSubmit = async (values: OrderDeliveryFormValues) => {
    onSubmit(transformFromDeliveryFormValues(values));
  };

  const formInitialValues = useMemo(() => {
    if (!initialValues) return {};
    return transformToDeliveryFormValues(initialValues);
  }, [initialValues]);

  return (
    <Form
      initialValues={formInitialValues}
      layout="vertical"
      onFinish={handleSubmit}
      size="large"
    >
      <DeliveryFormFields />
      <Button type="primary" htmlType="submit" block className="mt-6">
        Submit
      </Button>
    </Form>
  );
};

const InputDelivery = () => {
  const [show, setShow] = useState(false);
  const { order, setOrder } = useContext(OrderContext);

  const onChange = async (value: OrderDelivery) => {
    if (!order.isNew) return;

    setOrder({
      ...order,
      delivery: value,
    });
    setShow(false);
  };

  if (order.orderType !== OrderType.Indent) return null;

  const deliveryDateTimeStr = (() => {
    const { date, time } = order.delivery?.deliveryDateTime ?? {};
    return [date, time].filter(Boolean).join(' ') ?? '-';
  })();

  const addressStr = (() => {
    const { streetAddressLine1, streetAddressLine2 } =
      order.delivery?.deliveryAddress ?? {};
    return [streetAddressLine1, streetAddressLine2].filter(Boolean).join(' ');
  })();

  const phoneNumberStr = order.delivery
    ? `${order.delivery?.mobileCountryCode} ${order.delivery?.mobileNumber}`
    : '';

  return (
    <>
      <div className="mt-1">
        <div className="md:flex">
          <div className="flex-1">
            <Row>
              <Col span={12} md={6}>
                <span className="mr-2">Name:</span>
                <b>{order.delivery?.recipientName}</b>
              </Col>
              <Col span={12} md={8}>
                <span className="mr-2">Telp:</span>
                <b>{phoneNumberStr}</b>
              </Col>
              <Col span={24} md={10}>
                <span className="mr-2">Waktu ambil:</span>
                <b>{deliveryDateTimeStr}</b>
              </Col>
            </Row>
            <div>
              <span className="mr-2">Alamat:</span>
              <b>{addressStr}</b>
            </div>
          </div>
          <Button
            type="primary"
            onClick={() => setShow(true)}
            disabled={!order.isNew}
          >
            <div className="flex items-center gap-1">
              <EditOutlined />
              <p>Indent</p>
            </div>
          </Button>
        </div>
      </div>
      <Modal
        title="Detail Indent"
        open={show}
        destroyOnClose
        footer={null}
        onCancel={() => setShow(false)}
      >
        <InputDeliveryModal
          initialValues={order.delivery}
          onSubmit={onChange}
        />
      </Modal>
    </>
  );
};

export default InputDelivery;
